import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAdminContext } from '../context/AdminContext';
import { useExpertContext } from '../context/ExpertContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiClock, FiCheck, FiX, FiEye, FiDownload, FiLogOut, FiMapPin, FiMail, FiTrash2, FiMessageSquare, FiStar } = FiIcons;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAdminContext();
  const { experts, reviews, updateExpertStatus, deleteExpert, updateReviewStatus, deleteReview, refreshExperts } = useExpertContext();
  
  const [activeView, setActiveView] = useState('experts'); // 'experts' or 'reviews'
  const [selectedStatus, setSelectedStatus] = useState('pending');
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [showExpertModal, setShowExpertModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) navigate('/admin');
    else refreshExperts();
  }, [isAuthenticated, navigate]);

  const filteredExperts = experts.filter(e => e.status === selectedStatus);
  const filteredReviews = reviews.filter(r => r.status === selectedStatus);

  if (!isAuthenticated) return null;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Admin Portal</h1>
          <p className="text-slate-500 font-medium">Manage network applications and feedback</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => { logout(); navigate('/'); }} className="bg-slate-100 text-slate-600 px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold hover:bg-red-50 hover:text-red-600 transition-colors">
            <SafeIcon icon={FiLogOut} /> Logout
          </button>
        </div>
      </div>

      {/* View Switcher */}
      <div className="flex gap-2 mb-8 bg-slate-100 p-1 rounded-2xl w-fit">
        <button 
          onClick={() => setActiveView('experts')}
          className={`px-6 py-2.5 rounded-xl font-black text-sm transition-all ${activeView === 'experts' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}
        >
          Experts ({experts.filter(e => e.status === 'pending').length})
        </button>
        <button 
          onClick={() => setActiveView('reviews')}
          className={`px-6 py-2.5 rounded-xl font-black text-sm transition-all ${activeView === 'reviews' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}
        >
          Reviews ({reviews.filter(r => r.status === 'pending').length})
        </button>
      </div>

      {/* Status Tabs */}
      <div className="grid grid-cols-3 gap-4 mb-8 max-w-2xl">
        {['pending', 'approved', 'rejected'].map(status => (
          <button 
            key={status} 
            onClick={() => setSelectedStatus(status)}
            className={`p-4 rounded-2xl border-2 transition-all text-left ${selectedStatus === status ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 bg-white'}`}
          >
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{status}</div>
            <div className="text-2xl font-black text-slate-900">
              {activeView === 'experts' 
                ? experts.filter(e => e.status === status).length 
                : reviews.filter(r => r.status === status).length}
            </div>
          </button>
        ))}
      </div>

      {/* Content List */}
      <div className="space-y-4">
        {activeView === 'experts' ? (
          filteredExperts.length === 0 ? (
            <EmptyState icon={FiUsers} label={`No ${selectedStatus} applications`} />
          ) : (
            filteredExperts.map(expert => (
              <ExpertRow 
                key={expert.id} 
                expert={expert} 
                onView={() => { setSelectedExpert(expert); setShowExpertModal(true); }}
                onDelete={() => deleteExpert(expert.id)}
                onApprove={() => updateExpertStatus(expert.id, 'approved')}
                onReject={() => updateExpertStatus(expert.id, 'rejected')}
              />
            ))
          )
        ) : (
          filteredReviews.length === 0 ? (
            <EmptyState icon={FiMessageSquare} label={`No ${selectedStatus} reviews`} />
          ) : (
            filteredReviews.map(review => (
              <ReviewRow 
                key={review.id} 
                review={review}
                expertName={experts.find(e => e.id === review.expert_id)?.full_name || 'Unknown Expert'}
                onDelete={() => deleteReview(review.id)}
                onApprove={() => updateReviewStatus(review.id, 'approved')}
                onReject={() => updateReviewStatus(review.id, 'rejected')}
              />
            ))
          )
        )}
      </div>

      {/* Expert Modal */}
      {showExpertModal && selectedExpert && (
        <ExpertDetailModal 
          expert={selectedExpert} 
          onClose={() => setShowExpertModal(false)}
          onApprove={() => { updateExpertStatus(selectedExpert.id, 'approved'); setShowExpertModal(false); }}
          onDelete={() => { deleteExpert(selectedExpert.id); setShowExpertModal(false); }}
        />
      )}
    </div>
  );
};

// Sub-components for cleaner code
const EmptyState = ({ icon, label }) => (
  <div className="expert-card text-center py-12">
    <SafeIcon icon={icon} className="text-4xl text-slate-200 mx-auto mb-4" />
    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">{label}</p>
  </div>
);

const ExpertRow = ({ expert, onView, onDelete, onApprove, onReject }) => (
  <motion.div layout className="expert-card flex items-center justify-between group">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 group-hover:bg-blue-600 group-hover:text-white transition-colors">
        <SafeIcon icon={FiUsers} className="text-xl" />
      </div>
      <div>
        <h3 className="font-black text-slate-900">{expert.full_name}</h3>
        <div className="flex items-center gap-3 text-xs font-bold text-slate-400 uppercase mt-1">
          <span className="flex items-center gap-1"><SafeIcon icon={FiMapPin} /> {expert.governorate}</span>
          <span className="flex items-center gap-1"><SafeIcon icon={FiMail} /> {expert.email}</span>
        </div>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <button onClick={onView} className="p-2.5 rounded-xl bg-slate-50 text-slate-600 hover:bg-slate-200"><SafeIcon icon={FiEye} /></button>
      <button onClick={() => window.confirm('Delete this entry?') && onDelete()} className="p-2.5 rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white"><SafeIcon icon={FiTrash2} /></button>
      {expert.status === 'pending' && (
        <>
          <button onClick={onApprove} className="p-2.5 rounded-xl bg-green-50 text-green-600 hover:bg-green-600 hover:text-white"><SafeIcon icon={FiCheck} /></button>
          <button onClick={onReject} className="p-2.5 rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white"><SafeIcon icon={FiX} /></button>
        </>
      )}
    </div>
  </motion.div>
);

const ReviewRow = ({ review, expertName, onDelete, onApprove, onReject }) => (
  <motion.div layout className="expert-card group">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-black text-slate-900">{review.reviewer_name}</span>
          <span className="text-slate-300">→</span>
          <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{expertName}</span>
        </div>
        <div className="flex gap-0.5 mb-3">
          {[...Array(5)].map((_, i) => (
            <SafeIcon key={i} icon={FiStar} className={`text-[10px] ${i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />
          ))}
        </div>
        <p className="text-slate-600 text-sm font-medium leading-relaxed italic">"{review.comment}"</p>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <button onClick={() => window.confirm('Delete this review?') && onDelete()} className="p-2.5 rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white"><SafeIcon icon={FiTrash2} /></button>
        {review.status === 'pending' && (
          <>
            <button onClick={onApprove} className="p-2.5 rounded-xl bg-green-50 text-green-600 hover:bg-green-600 hover:text-white"><SafeIcon icon={FiCheck} /></button>
            <button onClick={onReject} className="p-2.5 rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white"><SafeIcon icon={FiX} /></button>
          </>
        )}
      </div>
    </div>
  </motion.div>
);

const ExpertDetailModal = ({ expert, onClose, onApprove, onDelete }) => (
  <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl">
      <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <h2 className="text-xl font-black text-slate-900">Application Details</h2>
        <button onClick={onClose} className="p-2 hover:bg-white rounded-xl transition-colors"><SafeIcon icon={FiX} className="text-xl text-slate-400" /></button>
      </div>
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div><label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Full Name</label><p className="font-bold text-slate-900">{expert.full_name}</p></div>
          <div><label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Email</label><p className="font-bold text-slate-900">{expert.email}</p></div>
        </div>
        <div><label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Bio</label><p className="text-slate-600 text-sm leading-relaxed mt-2">{expert.bio || 'No bio provided.'}</p></div>
        <div className="flex gap-3 pt-4">
          {expert.status !== 'approved' && <button onClick={onApprove} className="flex-1 btn-primary text-white py-4 rounded-2xl font-bold">Approve</button>}
          <button onClick={() => window.confirm('Delete entry?') && onDelete()} className="flex-1 bg-red-600 text-white py-4 rounded-2xl font-bold">Delete Entry</button>
        </div>
      </div>
    </motion.div>
  </div>
);

export default AdminDashboard;